import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarClock, Loader2, MapPin, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import EmptyState from '../ui/empty-state';
import { SkeletonCards } from '../Brand/BrandLoading';
import { ukDate } from '../../lib/format';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK = { date: '', start_time: '', end_time: '', all_day: false, location: '', note: '' };

const prettyTime = (value) => {
  if (!value) return '';
  const [hours, minutes] = value.split(':').map(Number);
  const period = hours >= 12 ? 'pm' : 'am';
  const display = hours % 12 || 12;
  return minutes ? `${display}:${String(minutes).padStart(2, '0')}${period}` : `${display}${period}`;
};

// Staff tell us when they are not available. Everyone is free unless they say so.
export const MyAvailability = ({ token }) => {
  const [busyTimes, setBusyTimes] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/staff/busy-times`, { headers });
      setBusyTimes(res.data?.busy_times || []);
    } catch (err) {
      setBusyTimes([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const openNew = () => {
    setForm(BLANK);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({
      date: row.date || '',
      start_time: row.start_time || '',
      end_time: row.end_time || '',
      all_day: !!row.all_day,
      location: row.location || '',
      note: row.note || '',
    });
    setEditingId(row.id);
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/staff/busy-times/${editingId}`, form, { headers });
        toast.success('Busy time updated');
      } else {
        await axios.post(`${API}/staff/busy-times`, form, { headers });
        toast.success('Busy time added');
      }
      setShowForm(false);
      setForm(BLANK);
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save that');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm('Delete this busy time?')) return;
    try {
      await axios.delete(`${API}/staff/busy-times/${row.id}`, { headers });
      toast.success('Busy time deleted');
      await load();
    } catch (err) {
      toast.error('Could not delete that');
    }
  };

  return (
    <div className="space-y-4" data-testid="my-availability">
      <Card className="bg-white bd-card-accent">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-5 w-5 text-gold-deep" /> My availability
          </CardTitle>
          <p className="text-sm text-slate-600">
            You count as free unless you add a busy time here. Only you can see these.
          </p>
        </CardHeader>
        <CardContent>
          {!showForm ? (
            <Button className="min-h-12 w-full" onClick={openNew} data-testid="add-busy-time">
              <Plus className="mr-1 h-5 w-5" /> Add a busy time
            </Button>
          ) : (
            <div className="space-y-3" data-testid="busy-time-form">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Date</label>
                <Input type="date" value={form.date} className="min-h-12"
                       onChange={(e) => setForm({ ...form, date: e.target.value })}
                       data-testid="busy-date" />
              </div>

              <label className="flex min-h-12 items-center gap-3 rounded-[10px] border border-hair bg-page px-3">
                <Checkbox checked={form.all_day} data-testid="busy-all-day"
                          onCheckedChange={(value) => setForm({ ...form, all_day: !!value })} />
                <span className="text-sm font-medium text-ink">All day</span>
              </label>

              {!form.all_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">From</label>
                    <Input type="time" step="900" value={form.start_time} className="min-h-12"
                           onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                           data-testid="busy-start" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">To</label>
                    <Input type="time" step="900" value={form.end_time} className="min-h-12"
                           onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                           data-testid="busy-end" />
                  </div>
                  <p className="col-span-2 -mt-1 text-[11px] text-slate-500">
                    Times can run past midnight, for example 10pm to 1am.
                  </p>
                </div>
              )}

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Where (optional)
                </label>
                <Input value={form.location} className="min-h-12" placeholder="Postcode or town"
                       onChange={(e) => setForm({ ...form, location: e.target.value })}
                       data-testid="busy-location" />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">
                  Note (optional)
                </label>
                <Input value={form.note} className="min-h-12"
                       placeholder="For example: job with another company"
                       onChange={(e) => setForm({ ...form, note: e.target.value })}
                       data-testid="busy-note" />
              </div>

              <div className="flex gap-2">
                <Button className="min-h-12 flex-1" onClick={save} disabled={saving}
                        data-testid="busy-save">
                  {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                  {editingId ? 'Save changes' : 'Add busy time'}
                </Button>
                <Button variant="outline" className="min-h-12" data-testid="busy-cancel"
                        onClick={() => { setShowForm(false); setEditingId(null); }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {busyTimes === null ? (
        <SkeletonCards count={2} />
      ) : busyTimes.length === 0 ? (
        <EmptyState icon={CalendarClock} title="Nothing in the diary"
                    line="You are down as free. Add a busy time when you are not available."
                    testId="no-busy-times" />
      ) : (
        <div className="space-y-3">
          {busyTimes.map((row) => (
            <Card key={row.id} className="bg-white" data-testid={`busy-time-${row.id}`}>
              <CardContent className="flex flex-wrap items-start justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="font-bold text-ink">{ukDate(row.date)}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="text-[11px]">
                      {row.all_day ? 'All day'
                        : `${prettyTime(row.start_time)} – ${prettyTime(row.end_time)}`}
                    </Badge>
                    {row.location && (
                      <span className="flex items-center gap-1 text-xs text-slate-600">
                        <MapPin className="h-3 w-3" /> {row.location}
                      </span>
                    )}
                  </div>
                  {row.note && <p className="mt-1.5 text-sm text-slate-600">{row.note}</p>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="min-h-11"
                          onClick={() => openEdit(row)} data-testid={`busy-edit-${row.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="min-h-11"
                          onClick={() => remove(row)} data-testid={`busy-delete-${row.id}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyAvailability;
